import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DICTIONARIES,
  DIRECTION,
  ageFrom,
  formatDate,
  formatNumber,
  formatTime,
  interpolate,
  type Locale,
} from '@oxyflows/i18n';

type Dict = (typeof DICTIONARIES)['en'];

interface I18nValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Dict;
  setLocale: (locale: Locale) => void;
  /** Interpolating lookup: tx(t.dashboard.greeting, { name }) */
  tx: (template: string, values?: Record<string, string | number>) => string;
  formatDate: (value: string | Date | null | undefined) => string;
  formatTime: (value: string | Date, timeZone?: string) => string;
  formatNumber: (value: number) => string;
  ageFrom: typeof ageFrom;
  /** Patient display name in the reader's script when one is recorded. */
  personName: (p: {
    givenName: string;
    familyName: string;
    givenNameAlt?: string | null;
    familyNameAlt?: string | null;
  }) => string;
}

const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = 'oxy.locale';

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {
    // Private mode or blocked storage: fall through to the browser preference.
  }
  return navigator.language?.startsWith('ar') ? 'ar' : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const dir = DIRECTION[locale];

  useEffect(() => {
    // Direction and language belong on <html>, so native form controls, text
    // selection, scrollbars and logical CSS properties all follow.
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A remembered preference is a convenience, not a requirement.
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      dir,
      t: DICTIONARIES[locale],
      setLocale,
      tx: (template, values) => interpolate(template, values),
      formatDate: (v) => formatDate(v, locale),
      formatTime: (v, tz) => formatTime(v, locale, tz),
      formatNumber: (v) => formatNumber(v, locale),
      ageFrom,
      personName: (p) => {
        // Show the Arabic spelling to an Arabic reader when the clinic recorded
        // one; otherwise show what was actually entered. Never transliterate —
        // a guessed spelling of a patient's name is worse than the original.
        if (locale === 'ar' && p.givenNameAlt && p.familyNameAlt) {
          return `${p.givenNameAlt} ${p.familyNameAlt}`;
        }
        return `${p.givenName} ${p.familyName}`;
      },
    }),
    [locale, dir, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
